/*--------------------------------------------------------------------------------------------
Program     :  LeftSidebar.js
Description :  Main program for Map and Track LeftSide data process
Date        :  29/08/2024 
Created By  :  Fode/Jelbin
---------------------------------------------------------------------------------------------
TicketNo         Author          Date            ChangeDesc
---------------------------------------------------------------------------------------------
29082024        - Fode/Jelbin     -  29/08/2024        Created
---------------------------------------------------------------------------------------------*/
import React, { useState, useEffect, useCallback } from 'react';
import MainService from "../service/MainService";
import {
  Accordion, AccordionSummary, Radio, Button,
  TextField, Typography, Box, Snackbar
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSnackbar } from "notistack";
import io from 'socket.io-client';
import { WEBSOCKET_URL } from './Socketconfig';
import AuthService from "../service/AuthService";
import CircularProgress from '@mui/material/CircularProgress';
import { useMapTrackInfo } from "../context/Maptrackcontext";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TimeCalculationService from '../service/TimeCalculationService';


const strongDistinctColors = [
  '#FF0000', // Red
  '#00FF00', // Lime
  '#0000FF', // Blue
  '#FFFF00', // Yellow
  '#FF00FF', // Magenta
  '#00FFFF', // Cyan
  '#800000', // Maroon
  '#008000', // Green
  '#000080', // Navy
  '#808000', // Olive
  '#800080', // Purple
  '#008080', // Teal
  '#FFA500', // Orange
  '#FF4500', // OrangeRed
  '#DA70D6', // Orchid
  '#32CD32', // LimeGreen
  '#6495ED', // CornflowerBlue
  '#CD5C5C', // IndianRed
  '#9370DB', // MediumPurple
  '#00FA9A', // MediumSpringGreen
  '#4682B4', // SteelBlue
  '#D2691E', // Chocolate
  '#FF7F50', // Coral
  '#6A5ACD', // SlateBlue
  '#7FFF00', // Chartreuse
  '#4169E1', // RoyalBlue
  '#8A2BE2', // BlueViolet
  '#DC143C', // Crimson
  '#00CED1', // DarkTurquoise
  '#9932CC', // DarkOrchid
  '#8B0000', // DarkRed
  '#8B008B', // DarkMagenta
  '#483D8B', // DarkSlateBlue
  '#2F4F4F', // DarkSlateGray
  '#556B2F', // DarkOliveGreen
  '#8B4513', // SaddleBrown
  '#B8860B', // DarkGoldenRod
  '#A0522D', // Sienna
  '#006400', // DarkGreen
  '#FF8C00', // DarkOrange
  '#9400D3', // DarkViolet
  '#FF1493', // DeepPink
  '#1E90FF', // DodgerBlue
  '#B22222', // FireBrick
  '#228B22', // ForestGreen
  '#4B0082', // Indigo
  '#20B2AA', // LightSeaGreen
  '#0000CD', // MediumBlue
  '#BA55D3', // MediumOrchid
  '#C71585', // MediumVioletRed
  '#191970', // MidnightBlue
  '#7B68EE', // MediumSlateBlue
  '#50C878', // Emerald
  '#6B8E23', // OliveDrab
  '#3CB371', // MediumSeaGreen
  '#9966CC', // Amethyst
  '#808080', // Gray
  '#000000'  // Black
];

const paleDistinctColors = ['#FFC0CB', '#FFD700', '#FA8072', '#FF69B4', '#BDB76B',
  '#DAA520', '#DDA0DD', '#53868B', '#8B7355', '#D2B48C', '#66CDAA', '#CD853F',
  '#DEB887', '#F4A460', '#48D1CC', '#F08080', '#BC8F8F', '#DB7093', '#F0E68C',
  '#EEE8AA', '#E9967A', '#8FBC8F', '#00BFFF', '#98FB98', '#7CFC00', '#F0FFF0',
  '#E6E6FA', '#FFF0F5', '#F0FFFF', '#FFFACD', '#D8BFD8', '#DDA0DD', '#B0C4DE',
  '#E0FFFF', '#FAFAD2', '#FFE4E1', '#F5DEB3', '#FFDAB9', '#AFEEEE', '#D3D3D3',
  '#90EE90', '#FFB6C1'];

const colorManager = (() => {
  let colorIndex = 0;
  const allColors = [...strongDistinctColors, ...paleDistinctColors];
  return {
    getColor: () => {
      const color = allColors[colorIndex];
      colorIndex = (colorIndex + 1) % allColors.length;
      return color;
    },
    reset: () => {
      colorIndex = 0;
    }
  };
})();


// Component for selecting date range with automatic end date calculation
const DateRangePicker = ({ dateRange, onDateChange, resetComponent }) => {
  // Handler for start date changes - automatically sets end date 6 days later
  const handleStartDateChange = (startDate) => {
    if (startDate) {
      const start = new Date(startDate);
      const defaultEnd = new Date(start);
      defaultEnd.setDate(start.getDate() + 6);
      const formattedEndDate = defaultEnd.toISOString().split("T")[0];
      onDateChange({
        start: startDate,
        end: formattedEndDate,
      });
      resetComponent();
    }
  };

  // Handler for end date changes
  const handleEndDateChange = (endDate) => {
    if (endDate) {
      onDateChange({ ...dateRange, end: endDate });
      resetComponent();
    }
  };

  return (
    <Box sx={{ marginBottom: 2 }}>
      <TextField
        type="date"
        value={dateRange.start}
        onChange={(e) => handleStartDateChange(e.target.value)}
        fullWidth
        margin="normal"
        InputLabelProps={{ shrink: true }}
        label="Start Date"
      />
      <TextField
        type="date"
        value={dateRange.end}
        onChange={(e) => handleEndDateChange(e.target.value)}
        fullWidth
        margin="normal"
        InputLabelProps={{ shrink: true }}
        label="End Date"
      />
    </Box>
  );
};

// Component for displaying selectable source items in accordion style
const SourceAccordion = ({ source, isSelected, onSelect, expanded, onExpand }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'white', mb: 1, p: 1 }}>
    <Radio
      checked={isSelected}
      onChange={onSelect}
      inputProps={{ 'aria-label': `Select ${source.SOURCEID}` }}
    />
    <Typography>{source.MAPNAME}</Typography>
  </Box>
);

// Main LeftSidebar component for handling map data and visualization
const LeftSidebar = ({ dateRange, onDateChange, setSelectedTraveler, setSelectedSourceMain,
  setdayCapacityValue, setmessageFields, setpopupFields, onPOIDataChange, isVisible, setdurationFields, settableUniqueName,
  setfieldPOIsave, setstartTimeField, setendTimeField, setfielduserstartTime, setTskTravelTimeField, setfixschedulefield,
  mapVisibilityDate, setSelectedSourceMapname, seteditablefield, setsubformName, setZDATERANGE,
  setMainFormName, setParticipantsData, setstatuscolorField }) => { //setFinalTravelTimeData,
  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  // const [expandedStates, setExpandedStates] = useState([]);
  const [searchData, setSearchData] = useState([]);
  const [travelMainSource, setTravelMainSource] = useState("");
  const [travelData, setTravelData] = useState([]);
  //const [combinedData, setCombinedData] = useState([]);
  const [travelerAddressFields, setTravelerAddressFields] = useState([]);
  const [detailAddressFields, setDetailAddressFields] = useState([]);
  const [SelectedDayCapacity, setSelectedDayCapacity] = useState("");
  const [poiData, setPOIData] = useState([]);
  const [finalPOIData, setFinalPOIData] = useState([]);
  const [poiSource, setPOISource] = useState([]);
  const [ZDATERANGELeft, setZDATERANGELeft] = useState(''); // Add this state variable
  const [isSourcesFetched, setIsSourcesFetched] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [notification, setNotification] = useState(null);
  const [socket, setSocket] = useState(null);
  const [user, setUser] = React.useState(AuthService.getCurrentUser());
  const [POIFields, setPOIFields] = useState();
  const [dataloaded, setDataLoaded] = useState(false);
  const { dataModified, setDataModified, combinedData, setCombinedData, setTimeATPOI,
    timeATPOI, reSearch, setResearch, participantsFields, setparticipantsFields,
    setparticipantsTimeFields, setmergeparticipantFields, setForMinTime, setTrvlTimePercnt,
    participantTaskTime, setParticipantTaskTime, openCalender, setHolidayPayload,
    fielduserHide, setfielduserHide, finalTravelTimeData, setFinalTravelTimeData,
    basePayload, setbasePayload, setTravelbuttonEnable, setSavebuttonEnable,userReadOnly} = useMapTrackInfo();
  const [dlgSaveopen, setdlgSaveopen] = useState(false);
  const [selectedIndex, setselectedIndex] = useState(null)
  const [savePOIField, setsavePOIField] = useState("")
  //const [finalTravelTimeDataLeft, setFinalTravelTimeDataLeft] = useState([]);
  const [tskTravelTimeFieldLeft, setTskTravelTimeFieldLeft] = useState("")
  const [fielduserstartTimeLeft, setfielduserstartTimeLeft] = useState("")
  const [startTimeFieldLeft, setstartTimeFieldLeft] = useState("")
  const [endTimeFieldLeft, setendTimeFieldLeft] = useState("")
  const [fixschedulefieldLeft, setfixschedulefieldLeft] = useState("")
  const [initialCombinedData, setInitialCombinedData] = useState([])
  const [dataWithTravelTimes, setDataWithTravelTimes] = useState([])
  const [travelerFieldName, setTravelerFieldName] = useState('');
  const [durationFieldVal, setDurationFieldVal] = useState('');
  const [tableUniqueNameVal, setTableUniqueNameVal] = useState([]);
  const [combinedDataLeft, setCombinedDataLeft] = useState([]);
  const [participantsDataLeft, setParticipantsDataLeft] = useState([]);
  const [mergeparticipantFieldsLeft, setmergeparticipantFieldsLeft] = useState([]);
  const [altTravelerAddressFields, setAltTravelerAddressFields] = useState([]);
  const [alternateAddressData, setalternateAddressData] = useState([]);
  const [altTravelerAltDateField, setAltTravelerAltDateField] = useState("");
  const [altTravelerDaystartField, setaltTravelerDaystartField] = useState("");
  const [altTravelerDayendField, setaltTravelerDayendField] = useState("");
  const [finalDataValue, setFinalDataValue] = useState([]);
  const [selectedSourceMapnameLeft, setSelectedSourceMapnameLeft] = useState("")

  // Initialize WebSocket connection for real-time notifications
  useEffect(() => {
    const newSocket = io(WEBSOCKET_URL, { query: { userName: user.username } });
    setSocket(newSocket);

    // Socket event handlers
    newSocket.on('connect', () => {
      console.log("Connected to WebSocket server");
    });

    newSocket.on('user_notification', (data) => {
      setNotification(data.message);
    });

    newSocket.on('disconnect', () => {
      console.log("Disconnected from WebSocket server");
    });

    return () => {
      console.log("Closing WebSocket connection");
      newSocket.close();
    };
  }, [user.username, enqueueSnackbar]);

  useEffect(() => {
    if (reSearch) {
      handleSearchClick();
      setResearch(false);
    }
  }, [reSearch]);


  // Fetch source data on component mount
  const fetchSources = useCallback(() => {
    if (!isSourcesFetched) {
      MainService.getMainSource()
        .then((response) => {
          setSources(response.data.value);
          setIsSourcesFetched(true);
        })
        .catch((err) => {
          setIsSourcesFetched(false);
        });
    }
  }, [isSourcesFetched]);

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  useEffect(() => {
    if (travelMainSource.length !== 0) {
      setdayCapacityValue(travelMainSource[0]?.DAYCAPACITY);
    }
  }, [travelMainSource]);


  // Updated useEffect to combine Agent and details

  useEffect(() => {
    if (searchData.length === 0 || travelData.length === 0 || selectedSource === null) return;
    var travelDataSorted = [];

    // set all the dummy travelers in the first of the traveller payload based on the  fielduserHide field. 
    if (fielduserHide !== null && fielduserHide !== undefined) {
      travelDataSorted = [...travelData].sort((firstUser, nextuser) => {
        return (nextuser[fielduserHide] === 'Y') - (firstUser[fielduserHide] === 'Y');
      });
    }
    else {
      travelDataSorted = travelData;
    }

    const sourceName = sources[selectedSource];
    setSelectedSourceMain(sourceName.ENAME);
    setSelectedSourceMapname(sourceName.MAPNAME);
    setSelectedSourceMapnameLeft(sourceName.MAPNAME);
    setSelectedDayCapacity(travelData.DAYCAPACITY);

    if (!sourceName?.ZMED_MAPFORMSCOL_SUBFORM) {
      console.error("Invalid source structure");
      return;
    }

    const travelerField = sourceName.ZMED_MAPFORMSCOL_SUBFORM.find(item => item.TRAVELLER === "Y");
    if (!travelerField) {
      return;
    }
    const travelerFieldName = travelerField.NAME;
    const messageFields = sourceName.ZMED_MAPFORMSCOL_SUBFORM.filter(item => item.MESSAGE === "Y");
    const popupFields = sourceName.ZMED_MAPFORMSCOL_SUBFORM.filter(item => item.POPUP === "Y");
    const durationField = sourceName.ZMED_MAPFORMSCOL_SUBFORM.filter(item => item.DURATION === "Y");
    const tableUnique = sourceName.ZMED_MAPFORMSCOL_SUBFORM.filter(item => item.UKEY === "Y");
    const tableUniqueNameVal = tableUnique.map((field) => field.NAME);
    const partipantsField = sourceName.ZMED_MAPFORMSCOL_SUBFORM.filter(item => item.PARTICIPANTS === "Y");
    const partipantsFieldVal = partipantsField.map((field) => field.NAME);
    const partipantTrvlTmFlds = sourceName.ZMED_MAPFORMSCOL_SUBFORM.filter(item => item.PARTICIPANTSTTIME === "Y");
    const partipantTrvlTmFldsVal = partipantTrvlTmFlds.map((field) => field.NAME);
    const statuscolor = sourceName.ZMED_MAPFORMSCOL_SUBFORM.filter(item => item.STATUSCOLOR === "Y");
    const statuscolorVal = statuscolor.map((field) => field.NAME);

    setTravelerFieldName(travelerFieldName);
    setDurationFieldVal(durationField[0].NAME);
    setTableUniqueNameVal(tableUniqueNameVal);
    setSelectedTraveler(travelerFieldName);
    setmessageFields(messageFields);
    setpopupFields(popupFields);
    setdurationFields(durationField);
    settableUniqueName(tableUniqueNameVal);
    setparticipantsFields(partipantsFieldVal);
    setparticipantsTimeFields(partipantTrvlTmFldsVal);
    setstatuscolorField(statuscolorVal[0]);

    //Merge partipantsFieldVal & partipantTrvlTmFldsVal 
    const mergedparticipantsArray = partipantsFieldVal.map((userLogin, index) => ({
      ZPARTCIPANTFILED: userLogin,
      ZPARTTRVLTIMEFILED: partipantTrvlTmFldsVal[index],
    }));

    setmergeparticipantFields(mergedparticipantsArray);
    setmergeparticipantFieldsLeft(mergedparticipantsArray);

    const createInitialData = async (partipantsFieldVal) => {
      const initialData = [];

      for (const traveler of travelDataSorted) {
        //const color = getColor();
        const color = colorManager.getColor();
        const travelerAddress = TimeCalculationService.getFullAddress(traveler, travelerAddressFields);
        let travelerCoords = traveler.coordinates;

        if (!travelerCoords && travelerAddress) {
          travelerCoords = await TimeCalculationService.getCoordinates(travelerAddress);
        }

        const details = [];
        const relevantSearchData = searchData.filter(detail =>
          detail[travelerFieldName] === traveler[travelerFieldName]
        );

        for (const detail of relevantSearchData) {
          const detailAddress = TimeCalculationService.getFullAddress(detail, detailAddressFields);

          let detailCoords = detail.coordinates;
          if (!detailCoords && detailAddress) {
            detailCoords = await TimeCalculationService.getCoordinates(detailAddress);
          }
          const travelTimeTo = TimeCalculationService.parseTime(detail[tskTravelTimeFieldLeft]);

          details.push({
            ...detail,
            color,
            coordinates: detailCoords,
            recordchange: false,
            travelTimeTo,
            participantFinalTrvltime: finalTravelTimeData.filter(data =>
              data.TRAVLRTYPE === "Participant" &&
              data.TEAMLEADER === detail[travelerFieldName] &&
              data.TRAVELDATE === detail[ZDATERANGELeft] &&
              partipantsFieldVal.some(field => detail[field] === data.TRAVELER)
            ) // check if data.TRAVELLER exists in any participant field of current detail
          });
        }
        
        const totalTime = details.reduce((sum, detail) => {
          //const detailDate = new Date(detail[ZDATERANGELeft]).toISOString().split('T')[0];
          const detailDate = detail[ZDATERANGELeft].split('T')[0]
          if (detailDate === mapVisibilityDate) {
            return sum + detail.travelTimeTo + TimeCalculationService.parseTime(detail[durationField[0].NAME]);
          }
          return sum;
        }, 0);
        initialData.push({
          ...traveler,
          details,
          expanded: false,
          color,
          coordinates: travelerCoords,
          totalTime,
          userPOI: "",
          userPOIChange: false,
          finalTravelTime: finalTravelTimeData.filter(data =>
            data.TEAMLEADER === traveler[travelerFieldName]
          )
        });
      }

      // Logic for Alternate Address
      if (alternateAddressData !== undefined) {
        const ZinitialDataWithAltAddress = await handleAlternateaddressMapping(initialData);
        setInitialCombinedData(ZinitialDataWithAltAddress);
      }
      else {
        setInitialCombinedData(initialData);
      }
      console.log("Step 1 Complete", initialData);
    };

    createInitialData(partipantsFieldVal);
  }, [searchData, travelData, selectedSource]);

  console.log("combinedDataLeft", combinedData);

  useEffect(() => {
    if (!initialCombinedData || !durationFieldVal || !travelerFieldName) return;
    var clonedData = [];
    if (combinedData.length === 0) {
      clonedData = structuredClone(initialCombinedData);
    }
    else {
      clonedData = structuredClone(combinedData);
    }
    const processFinaltravelTime = async () => {
      const withTravelTimes = await handleAddFinaltravelTime(
        clonedData,
        durationFieldVal,
        travelerFieldName
      );
      console.log("Step 2 Complete", withTravelTimes, clonedData);
      setDataWithTravelTimes(withTravelTimes);
    };

    processFinaltravelTime();
  }, [initialCombinedData, durationFieldVal, travelerFieldName]);


  useEffect(() => {
    if (!dataWithTravelTimes || !tableUniqueNameVal) return;

    const calculateStartEndTimes = async () => {
      const finalData = [];

      for (const agent of dataWithTravelTimes) {
        if (!agent) continue;

        // Preserve original details with travel times
        const originalDetails = [...agent.details];

        const selectedDateTasks = originalDetails.filter(subitem =>
          subitem[ZDATERANGELeft].startsWith(mapVisibilityDate)
        );
        try {
          const tasksWithNewTimes = await new Promise((resolve, reject) => {
            try {
              const result = TimeCalculationService.recalculateTaskTimes(
                selectedDateTasks,
                startTimeFieldLeft,
                endTimeFieldLeft,
                durationFieldVal,
                agent[fielduserstartTimeLeft],
                tskTravelTimeFieldLeft,
                fixschedulefieldLeft,
                mapVisibilityDate,
                ZDATERANGELeft
              );
              resolve(result);
            } catch (error) {
              reject(error);
            }
          });
          // Map back to original details preserving travel times
          const updatedDetails = originalDetails.map(originalItem => {
            const matchingTask = tasksWithNewTimes.find(task =>
              task[tableUniqueNameVal[0]] === originalItem[tableUniqueNameVal[0]]);
            if (matchingTask) {
              return {
                ...originalItem,
                [startTimeFieldLeft]: matchingTask[startTimeFieldLeft],
                [endTimeFieldLeft]: matchingTask[endTimeFieldLeft],
                //recordchange: true,
                // Preserve original travel time values
                travelTimeTo: originalItem.travelTimeTo,
                [tskTravelTimeFieldLeft]: TimeCalculationService.minutesToTimeString(Math.round(originalItem.travelTimeTo * 60))
              };
            }
            return originalItem;
          });
          finalData.push({
            ...agent,
            details: updatedDetails
          });
        } catch (error) {
          console.error('Error calculating task times:', error);
          finalData.push(agent);
        }
      }
      console.log("finalCombinedData", finalData);
      console.log("finalData");
      // send the combineddata to context
      setCombinedData(finalData);
      setCombinedDataLeft(finalData);
      setFinalDataValue(finalData);
      if (basePayload.length === 0) {
        setbasePayload(finalData);
      }
      if (finalData.length !== 0) {
        setDataLoaded(false);
      }
    };
    calculateStartEndTimes();
    
  }, [dataWithTravelTimes, tableUniqueNameVal]);

  // This useffect will call when the combineddata payload fully created.
  // This useffect call the function participantTotalTaskTime  and it will create new payload 
  // with each partcipant Total Task time
  // usually this useEffect will call when change the TopBar Date. It should not happen when select the Calender
  useEffect(() => {
    if (finalDataValue.length > 0 && openCalender === false) {
      async function fetchparticipantTaskTimes() {
        const FinalPartiTaskTime = await TimeCalculationService.participantTotalTaskTime(travelerFieldName,
          ZDATERANGELeft, participantsDataLeft, mergeparticipantFieldsLeft, finalDataValue, durationFieldVal);
        setParticipantTaskTime(FinalPartiTaskTime);
      }

      fetchparticipantTaskTimes();
    }
  }, [finalDataValue]);

  // This function Call the calculateAgentSchedule function  only if finalTravelTime is empty
  // and update the combinedData payload
  const handleAddFinaltravelTime = async (resolvedCombinedData, durationFieldVal, selectedtravelerVal) => {
    const combinedDataModified = await Promise.all(resolvedCombinedData.map(async (agent) => {
      // Check if the details array is not empty
      if (agent.details && agent.details.length > 0 && 
          agent.ZGEM_DUMMYUSER !== 'Y' && userReadOnly !== "Y" ) {
        // Check if finalTravelTime is empty
        if (agent.finalTravelTime.length !== undefined) {
          const hasFinalTravelTimes = agent.finalTravelTime.some(finalTime =>
            finalTime.TRAVELDATE.slice(0, 10) === mapVisibilityDate &&
            finalTime.TRAVLRTYPE === "TeamLeader"
          );
          if (!hasFinalTravelTimes) {
            // setDataModified(true);
            // Call the calculateAgentSchedule function if finalTravelTime is empty
            const updatedData = await TimeCalculationService.calculateAgentSchedule(
              agent,
              durationFieldVal,
              fielduserstartTimeLeft,
              tskTravelTimeFieldLeft,
              finalTravelTimeData,
              selectedtravelerVal,
              mapVisibilityDate,
              ZDATERANGELeft,
              selectedSourceMapnameLeft
            );
            // Update tskTravelTimeField in the details of the agent
            updatedData.details.forEach((detail) => {
              if (detail.travelTimeTo !== undefined) {
                // Update REMINDTIME to the value of travelTimeTo
                detail[tskTravelTimeFieldLeft] = TimeCalculationService.minutesToTimeString(Math.round(detail.travelTimeTo * 60));
                // detail.recordchange = true;
              }
            });

            // Update the final travel Time Block with new value
            setFinalTravelTimeData(prevData => {
              const filteredData = prevData.filter(data =>
                !updatedData.finalTravelTime.some(finalData =>
                  data.TRAVELER === finalData.TRAVELER &&
                  data.MAPNAME === finalData.MAPNAME &&
                  data.TRAVELDATE.slice(0, 10) === finalData.TRAVELDATE.slice(0, 10) &&
                  data.TRAVLRTYPE === "TeamLeader"
                )
              );
              return [...filteredData, ...updatedData.finalTravelTime];
            });

            // Return the updated agent with new data
            return { ...agent, ...updatedData };

          } else {
            // Check if any tasks need travel time calculation(if it is a new task)
            const hasEmptyTravelTimes = agent.details.some(detail =>
              (detail.travelTimeTo === 0 || detail.travelTimeTo === null) &&
              detail[ZDATERANGELeft].slice(0, 10) === mapVisibilityDate
            );
            if (hasEmptyTravelTimes) {

              // Calculate all travel times at once
              const updatedData = await TimeCalculationService.calculateTravelTimeTo(
                agent,
                durationFieldVal,
                fielduserstartTimeLeft,
                tskTravelTimeFieldLeft,
                finalTravelTimeData,
                selectedtravelerVal,
                mapVisibilityDate,
                ZDATERANGELeft,
                "LeftSidebar",
                "",
                "",
                selectedSourceMapnameLeft
              );

              // Update the final travel Time Block with new value
              setFinalTravelTimeData(prevData => {
                const filteredData = prevData.filter(data =>
                  !updatedData.finalTravelTime.some(finalData =>
                    data.TRAVELER === finalData.TRAVELER &&
                    data.MAPNAME === finalData.MAPNAME &&
                    data.TRAVELDATE.slice(0, 10) === finalData.TRAVELDATE.slice(0, 10) &&
                    data.TRAVLRTYPE === "TeamLeader"
                  )
                );
                return [...filteredData, ...updatedData.finalTravelTime];
              });

              return { ...agent, ...updatedData };
            }
            else {
              // totalTime = (each detailblock travelTimeTo + each travltimeduration + finalTravlTime)
              var ZfinalTimeValue = 0;
              const ZfinalTravelTimeData = agent.finalTravelTime.filter(data => data.TRAVELER === agent[selectedtravelerVal] &&
                data.TRAVELDATE.startsWith(mapVisibilityDate) && data.TRAVLRTYPE === "TeamLeader")
              if (ZfinalTravelTimeData.length !== 0) {
                ZfinalTimeValue = TimeCalculationService.parseTime(ZfinalTravelTimeData[0].TRAVELTIME);
              }
              const totalTime = agent.details.reduce((sum, detail) => {
                //const detailDate = new Date(detail[ZDATERANGELeft]).toISOString().split('T')[0];
                const detailDate = detail[ZDATERANGELeft].split('T')[0]
                if (detailDate.startsWith(mapVisibilityDate)) {
                  const durationTime = TimeCalculationService.parseTime(detail[durationFieldVal]);
                  return sum + detail.travelTimeTo + durationTime;
                }
                return sum;
              }, 0);
              return { ...agent, totalTime: totalTime + ZfinalTimeValue };
            }

          }
        }
      }
      return agent;
    }));
    return combinedDataModified;
  };

  const handleAlternateaddressMapping = async (initialDataInput) => {
    const updatedData = [];
    for (const user of initialDataInput) {
      const { USERLOGIN } = user;
      const userAlternateAddresses = alternateAddressData.filter(
        addr => addr.USERLOGIN === USERLOGIN
      );
      const alternateAddress = [];
      if (userAlternateAddresses.length > 0) {
        for (const alt of userAlternateAddresses) {
          const fullAddress = altTravelerAddressFields.map(field => alt[field]).filter(Boolean).join(', ');
          const coordinates = await TimeCalculationService.getCoordinates(fullAddress);
          alternateAddress.push({
            ALTERDATE: alt[altTravelerAltDateField],
            ALTERCOORDINATE: coordinates,
            DAYSTART: alt[altTravelerDaystartField],
            DAYEND: alt[altTravelerDayendField]
          });
        }
      }
      // Add alternateAddresses to user object
      updatedData.push({
        ...user,
        alternateAddress
      });
    }
    return updatedData;
  }

  // Adding the coordinates & UNKdata with the POI payload and store into 'finalPOIData'

  useEffect(() => {
    if (poiData.length !== 0) {
      processPoiData();
    }
  }, [poiData]);

  // Read the finalPOIData and attach with the combined data
  useEffect(() => {
    if (finalPOIData.length !== 0 && combinedDataLeft) {
      handleMergePOIwithCombinedData();
    }
  }, [finalPOIData, combinedDataLeft]);


  if (!isVisible) {
    return null;
  }

  // Function to merge the POI with CombinedData when page gets load
  function handleMergePOIwithCombinedData() {
    const CombinedDatawithPOI = combinedDataLeft.map((user) => {
      // Update user details as before
      const updatedDetails = user.details.map((detail) => {
        const matchedPOI = finalPOIData.find((poi) => poi.UNKdata === detail[savePOIField]);
        if (matchedPOI) {
          return {
            ...detail,
            pointsOfInterest: [matchedPOI],
          };
        }
        return detail;
      });

      // Check if any finalTravelTime exists and loop through them for the userPOI
      let userPOIs = [];

      if (user.finalTravelTime && Array.isArray(user.finalTravelTime)) {
        user.finalTravelTime.forEach((travelTime) => {
          // Check if the USERPOI field in this travel time is not empty
          if (travelTime.USERPOI && travelTime.USERPOI !== "") {
            const matchedUserPOI = finalPOIData.find((poi) => poi.UNKdata === travelTime.USERPOI);
            if (matchedUserPOI) {
              const poiWithassignDate = {
                ...matchedUserPOI,
                POIASSIGNDATE: travelTime.TRAVELDATE.split('T')[0] //new Date(travelTime.TRAVELDATE).toISOString().split('T')[0]
              };
              userPOIs.push(poiWithassignDate);
            }
          }
        });
      }

      // Return the combineddata with details POI and User POI
      return {
        ...user,
        details: updatedDetails,
        pointsOfInterest: [...userPOIs], // Add the user POIs if any
      };
    });
    setCombinedData(CombinedDatawithPOI);
    setbasePayload(CombinedDatawithPOI);
  }

  const processPoiData = async () => {
    const POIFields = poiSource[0].ZMED_POI_FORM_COLV_SUBFORM.filter((item) => item.ADDRESS === "Y");
    const POIFieldsName = POIFields.map((field) => field.NAME);
    //const validPOIs = poiData.filter(poi => poi.ZIP && poi.STATE);
    const validPOIs = poiData.filter(poi => POIFieldsName.every(field => poi[field]));
    const processedPOIs = await Promise.all(validPOIs.map(async (poi) => {
      const address = TimeCalculationService.getFullAddress(poi, POIFieldsName);  //['ADDRESS', 'ADDRESS2', 'ADDRESS3', 'ZIP', 'COUNTRYNAME']
      // JKB-New change. Need to get the coordinates from priorty based on address
      const coordinates = await TimeCalculationService.getCoordinates(address);
      return {
        ...poi,
        coordinates,
      };
    }));

    const finalProcessedPOIs = processedPOIs.filter(poi => poi.coordinates);

    //Adding the new field (UNKdata) with finalProcessedPOIs payload to store the unique key fields
    //Find the list of UKEY = Y  field from poiSource.
    const ZUKEYFieldArray = poiSource[0].ZMED_POI_FORM_COLV_SUBFORM.filter((item) => item.UKEY === "Y");

    // find field name from ZUKEYFieldArray
    const ZUKEYFieldName = ZUKEYFieldArray.map((field) => field.NAME);

    // Add new field to the poiData with UNKEY fields
    const finalProcessedPOIsUpd = finalProcessedPOIs.map((mapdata) => {
      // Create UNKdata by joining values from fields in data2
      const unkDataValue = ZUKEYFieldName.map((unkfield) => mapdata[unkfield] || '').join('-');
      return { ...mapdata, UNKdata: unkDataValue };
    });
    setFinalPOIData(finalProcessedPOIsUpd);

    if (typeof onPOIDataChange === 'function') {
      onPOIDataChange(finalProcessedPOIsUpd);
    }
  };

  // Handle source selection
  const handleSourceSelect = (index) => {

    if (dataModified) {
      setdlgSaveopen(true);
      setselectedIndex(index);
    }
    else {
      handleResetData();
      setSelectedSource(index);
      //setExpandedStates(prev => prev.map((_, i) => i === index));
    }
  }

  // Handle search button click
  const handleSearchClick = () => {
    if (dataModified) {
      setdlgSaveopen(true);
    }
    else {
      handleResetData();
      handleSearchData();
    }
  };

  const handleSearchData = () => {
    colorManager.reset();
    setCombinedData([]);
    setCombinedDataLeft([]);
    setInitialCombinedData([]);
    setDataWithTravelTimes([]);
    setFinalDataValue([]);
    setDataLoaded(true);
    setTravelbuttonEnable(false);
    setSavebuttonEnable(false);
    const source = sources[selectedSource];
    const ZFORMNAME = source.ENAME;
    const ZMAPNAME = source.MAPNAME;
    const ZSUBFORMNAME = source.SUBENAME;
    const ZFROMDATE = dateRange.start;
    const ZTODATE = dateRange.end;


    // Notify the server about the selected source and date range
    if (socket) {
      socket.emit('select_source_and_date', {
        source: ZMAPNAME,
        dateRange: { start: ZFROMDATE, end: ZTODATE },
        userName: user.username
      });
    }

    // Find the unique field for the selected source
    const ZDATERANGE = source.ZMED_MAPFORMSCOL_SUBFORM.find(item => item.UNIKRECORD === "Y")?.NAME || '';
    //const ZFILTERBYFIELD = source.ZMED_MAPFORMSCOL_SUBFORM.find(item => item.FILTER === "Y")?.NAME || '';
    const ZSAVEPOI = source.ZMED_MAPFORMSCOL_SUBFORM.find(item => item.POI === "Y")?.NAME || '';
    const ZSAVESTARTTIME = source.ZMED_MAPFORMSCOL_SUBFORM.find(item => item.STARTTIME === "Y")?.NAME || '';
    const ZSAVEENDTIME = source.ZMED_MAPFORMSCOL_SUBFORM.find(item => item.ENDTIME === "Y")?.NAME || '';
    //const ZFILTERBY = source.ZMED_MAPFORMSCOL_SUBFORM.find(item => item.FILTER === "Y")?.FILTERBY || '';
    const ZFILTERBYArray = source.ZMED_MAPFORMSCOL_SUBFORM.filter(item => item.FILTER === "Y");
    const ZTSKTRAVELTIME = source.ZMED_MAPFORMSCOL_SUBFORM.find(item => item.TRAVELTIME === "Y")?.NAME || '';
    const ZFIXSCHEDULE = source.ZMED_MAPFORMSCOL_SUBFORM.find(item => item.FIXSCHEDULE === "Y")?.NAME || '';
    // const ZEDITABLE = source.ZMED_MAPFORMSCOL_SUBFORM.find(item => item.EDITABLE === "Y")?.NAME || '';
    const ZEDITABLEArray = source.ZMED_MAPFORMSCOL_SUBFORM
      .filter(item => item.EDITABLE === "Y")
      .map(item => item.NAME ? { NAME: item.NAME, TITLE: item.TITLE } : null)
      .filter(item => item !== null) || [];

    const ZFILTERBYFIELDArray = source.ZMED_MAPFORMSCOL_SUBFORM
      .filter(item => item.FILTER === "Y")
      .map(item => item.NAME && item.FILTERBY ? { NAME: item.NAME, FILTERBY: item.FILTERBY } : null)
      .filter(item => item !== null) || [];

    // Set the searchData to  state variables
    setZDATERANGE(ZDATERANGE);
    setZDATERANGELeft(ZDATERANGE);
    setfieldPOIsave(ZSAVEPOI);
    setsavePOIField(ZSAVEPOI);
    setstartTimeField(ZSAVESTARTTIME);
    setstartTimeFieldLeft(ZSAVESTARTTIME);
    setendTimeField(ZSAVEENDTIME);
    setendTimeFieldLeft(ZSAVEENDTIME);
    setTskTravelTimeField(ZTSKTRAVELTIME);
    setTskTravelTimeFieldLeft(ZTSKTRAVELTIME);
    setfixschedulefield(ZFIXSCHEDULE);
    setfixschedulefieldLeft(ZFIXSCHEDULE);
    seteditablefield(ZEDITABLEArray);
    setMainFormName(ZFORMNAME);
    setsubformName(ZSUBFORMNAME);
    // Fetch search data, travel main source, and traveller data simultaneously
    Promise.all([
      MainService.getsearchdata(ZFORMNAME, ZFROMDATE, ZTODATE, ZDATERANGE, ZFILTERBYFIELDArray),
      MainService.gettravelMainSource(ZMAPNAME),
      MainService.gettravellerData(ZMAPNAME),
      MainService.getparticipantsData(ZMAPNAME),
      MainService.getPOIsource(ZMAPNAME),
      MainService.getFinalTravelData(ZMAPNAME, ZFROMDATE, ZTODATE),
      MainService.getAlternatetrvlHdr(ZMAPNAME),
      MainService.getAlternatetrvldata(ZFROMDATE, ZTODATE, ZMAPNAME),
      MainService.getHolidaysource(ZMAPNAME)
    ]).then(([searchdataResponse, travelsourceResponse, traveldataResponse,
      participantsResponse, poisourceResponse, finaltravelDataResponse,
      alternatetrvlhdrResponse, alternatetrvlResponse, holidaysourceresponse]) => {
      // Update state with fetched data   
      setSearchData(searchdataResponse.data.value);
      if (searchdataResponse.data.value.length !== 0) {
        //setDataLoaded(false);
        setTravelMainSource(travelsourceResponse.data.value);
        setTravelData(traveldataResponse.data.value);
        setPOISource(poisourceResponse.data.value);
        setFinalTravelTimeData(finaltravelDataResponse.data.value);
        console.log("setFinalTravelTimeData",finaltravelDataResponse.data.value);
        //setFinalTravelTimeDataLeft(finaltravelDataResponse.data.value);
        setParticipantsData(participantsResponse.data.value);
        setParticipantsDataLeft(participantsResponse.data.value);
        setalternateAddressData(alternatetrvlResponse.data.value);

        // Process POI data
        const poiSource = poisourceResponse.data.value[0];
        if (poiSource !== undefined) {
          const ZPOIFIELDS = poiSource.ZMED_POI_FORM_COLV_SUBFORM.map((item) => item.NAME).join(", ");
          setPOIFields(ZPOIFIELDS);
          const ZPOIENAME = poiSource.ENAME;
          // POI Filter Array
          const ZPOIFILTERARRAY = poiSource.ZMED_POI_FORM_COLV_SUBFORM
            .filter(item => item.FILTER === "Y")
            .map(item => item.NAME && item.FILTERBY ? { NAME: item.NAME, FILTERBY: item.FILTERBY } : null)
            .filter(item => item !== null) || [];
          MainService.getPOIData(ZPOIENAME, ZPOIFIELDS, ZPOIFILTERARRAY)
            .then((poiResponse) => {
              setPOIData(poiResponse.data.value);
            })
            .catch((err) => console.error("Error fetching POI data:", err));
        }
        // Process Holiday Data
        if (holidaysourceresponse && holidaysourceresponse.data.value &&
          Array.isArray(holidaysourceresponse.data.value) && holidaysourceresponse.data.value.length > 0) {
          const holidaySource = holidaysourceresponse.data.value[0];
          if (holidaySource !== undefined) {
            //const ZHOLIDAYFIELDS = holidaySource.ZMED_HOLDAY_FORMCOLV_SUBFORM[0].NAME;
            const ZHLDFRMFLD = holidaySource.ZMED_HOLDAY_FORMCOLV_SUBFORM.find(field => field.FROMDATE === "Y").NAME;
            const ZHLDTOFLD = holidaySource.ZMED_HOLDAY_FORMCOLV_SUBFORM.find(field => field.TODATE === "Y").NAME;
            const ZHOLIDAYFORMENAME = holidaySource.ENAME;
            MainService.getholidayDetails(ZFROMDATE, ZTODATE, ZHOLIDAYFORMENAME, ZHLDFRMFLD, ZHLDTOFLD)
              .then((holidaydataresponse) => {
                setHolidayPayload(holidaydataresponse.data.value);
              })
              .catch((err) => console.error("Error fetching Holiday data:", err));
          }
        }
        else {
          console.error("Error fetching Holiday data:", holidaysourceresponse)
        }
        // Set address fields for travelers
        const newTravelerAddressFields = travelsourceResponse.data.value[0].ZMED_TRAVELERCOLVIEW_SUBFORM
          .filter(field => field.ADDRESS === "Y")
          .map(field => field.NAME);
        setTravelerAddressFields(newTravelerAddressFields);

        // Set address fields for alternate travelers
        if (alternatetrvlhdrResponse && alternatetrvlhdrResponse.data.value &&
          Array.isArray(alternatetrvlhdrResponse.data.value) && alternatetrvlhdrResponse.data.value.length > 0) {
          if (alternatetrvlhdrResponse.data.value.length !== 0) {
            const newAlternateAddressFields = alternatetrvlhdrResponse.data.value[0].ZMED_ASTAY_FORM_CVW_SUBFORM
              .filter(field => field.ADDRESS === "Y")
              .map(field => field.NAME);
            setAltTravelerAddressFields(newAlternateAddressFields);

            const altDateField = alternatetrvlhdrResponse.data.value[0].ZMED_ASTAY_FORM_CVW_SUBFORM
              .filter(field => field.ADATE === "Y")
              .map(field => field.NAME);
            setAltTravelerAltDateField(altDateField);

            const altDaystart = alternatetrvlhdrResponse.data.value[0].ZMED_ASTAY_FORM_CVW_SUBFORM
              .filter(field => field.DAYSTART === "Y")
              .map(field => field.NAME);
            setaltTravelerDaystartField(altDaystart);

            const altDayend = alternatetrvlhdrResponse.data.value[0].ZMED_ASTAY_FORM_CVW_SUBFORM
              .filter(field => field.DAYEND === "Y")
              .map(field => field.NAME);
            setaltTravelerDayendField(altDayend);
          }
        }
        else {
          console.error("Error fetching alternate data:", alternatetrvlhdrResponse)
        }
        // Set STARTTIME  fields for travelers
        const ZSTARTTIMEFIELD = travelsourceResponse.data.value[0].ZMED_TRAVELERCOLVIEW_SUBFORM
          .filter(field => field.STARTTIME === "Y")
          .map(field => field.NAME);
        setfielduserstartTime(ZSTARTTIMEFIELD[0])
        setfielduserstartTimeLeft(ZSTARTTIMEFIELD[0])

        // Set address fields for details
        const newDetailAddressFields = source.ZMED_MAPFORMSCOL_SUBFORM
          .filter(field => field.ADDRESS === "Y")
          .map(field => field.NAME);
        setDetailAddressFields(newDetailAddressFields);

        // set the Timeatpoi
        const ZTIMEATPOI = poisourceResponse.data.value[0].TIMEATPOI;
        setTimeATPOI(ZTIMEATPOI);

        // Set HIDETRAVELLER  fields for travelers
        const ZHIDETRAVELLER = travelsourceResponse.data.value[0].ZMED_TRAVELERCOLVIEW_SUBFORM
          .filter(field => field.HIDETRAVELLER === "Y")
          .map(field => field.NAME);
        setfielduserHide(ZHIDETRAVELLER[0]);

      }
      else {
        setDataLoaded(false);
        enqueueSnackbar(`There is no data within the selected date range. Please reselect the date and try again..`, {
          variant: "error",
        });
      }

    }).catch(err => {
      console.error("Error fetching data:", err);
      // TODO: Set error state and display to user
    });
  }

  // Add a function to handle closing the notification
  const handleCloseNotification = () => {
    setNotification(null);
  };

  const handleResetData = () => {
    setCombinedData([]);
    // onCombinedDataChange([]);
    setSelectedTraveler("");
    setSelectedSourceMain("");
    setdayCapacityValue("");
    setmessageFields([]);
    setpopupFields([]);
    onPOIDataChange([]);
    setdurationFields([]);
  }


  const handleDlgSaveYes = () => {
    if (selectedIndex === null) { // When clcik the serach button
      handleResetData();
      handleSearchData();
    }
    else { // When select the new option 
      handleResetData();
      setSelectedSource(selectedIndex);
    }
    setdlgSaveopen(false);
    setDataModified(false);
  }

  const handleDlgSaveNo = () => {
    setdlgSaveopen(false);
    setselectedIndex(null);
  }


  // Render the LeftSidebar component
  return (
    <>
      <Box sx={{ p: 2, bgcolor: '#e5e7eb', height: '100%', overflowY: 'auto' }}>
        <DateRangePicker dateRange={dateRange} onDateChange={onDateChange} resetComponent={handleResetData} />
        {sources.map((source, index) => (
          <SourceAccordion
            key={source.SOURCEID}
            source={source}
            isSelected={selectedSource === index}
            onSelect={() => handleSourceSelect(index)}
          // expanded={expandedStates[index]}
          // onExpand={handleAccordionChange(index)}
          />
        ))}
        <Box sx={{ p: 2, textAlign: "center" }}>
          <></>
          <Button variant="contained" fullWidth onClick={handleSearchClick}
            disabled={selectedSource === null || dataloaded} style={{ marginBottom: '5px' }}>Search</Button>
          {dataloaded ? (
            <div>
              Loading Data...
              <CircularProgress size={25} style={{ color: "red" }} />
            </div>
          ) : null}
        </Box>

        <Snackbar
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          open={!!notification}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          message={notification}
        />
      </Box>
      <Dialog open={dlgSaveopen}>
        <DialogTitle></DialogTitle>
        <DialogContent>
          <b>Unsaved changes exist. Do you want to proceed without saving the data?</b>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDlgSaveYes}>
            YES
          </Button>
          <Button onClick={handleDlgSaveNo}>NO</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(LeftSidebar);